import { Graphics } from '@inlet/react-pixi';
import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { QueueModal as PhoneQueueModal } from 'components/powerCallFlow/components/modals/queue';
import type { AnnouncementPayloadT } from 'components/powerCallFlow/components/types';
import ChatQueueModal from 'components/powerChatFlow/containers/modals/queue';
import { QueueModal as EmailQueueModal } from 'components/powerEmailFlow/components/modals/queue';
import { useFindQueue } from 'components/powerFlowShared/hooks/useFindQueue';
import { usePixiBadge } from 'components/powerFlowShared/hooks/usePixiBadge';
import { QueueModal as MessengerQueueModal } from 'components/settings/components/sections/dixaMessenger/views/flow/components/nodeTypeQueue/components/queueModal';
import type { AudioT } from 'components/shared/audio/api/types';
import { flowNodeColor, FlowType } from 'fable/flowEditor';
import { hexStringToNumber } from 'fable/flowEditor/components/flowManager/canvas/renderers/common/colors';
import type {
  ChatExtraData,
  EmailExtraData,
  PhoneExtraData,
} from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'fable/flowEditor/components/flowManager/canvas/types/node';
import { Text } from 'flow-builder/node/text';
import { TextBreak } from 'flow-builder/node/textBreak';
import { TextContent } from 'flow-builder/node/textContent';
import { TextEllipsis } from 'flow-builder/node/textEllipsis';

import type { OptionT } from '../../types';

export type QueueTimeout = {
  timeout_in_seconds: number;
  timeout_node_id: string;
};

export type AnnouncementAttributes = {
  max_announcement_position?: number;
  play_announcement: boolean;
};

export type CallbackAttributes = {
  announcement: null | AnnouncementPayloadT;
  digit?: null | string;
  from_number: null | string;
  timeout?: number;
};

export type Payload = {
  announcement_attributes?: AnnouncementAttributes;
  callbackInfo?: CallbackAttributes;
  gender?: string;
  language?: string | null;
  queue_timeout?: QueueTimeout;
  queue: null | OptionT;
  wait_music_v2?: AudioT;
};

type QueueAttributeFactoryProps = {
  withSettingsText: boolean;
  defaultQueueName?: string;
};
type AcceptableExtraData = PhoneExtraData | EmailExtraData | ChatExtraData;

type QueueNodeAttributes = GenericNodeAttributes<Payload, AcceptableExtraData>;
type QueueNodeProps = GenericNodeProps<Payload, AcceptableExtraData>;

const translatePayload: QueueNodeAttributes['translatePayload'] = (node) => {
  const { queue_timeout } = node.payload;

  return queue_timeout
    ? [
        {
          id: uuid(),
          connection: queue_timeout.timeout_node_id || uuid(),
          label: 'Timeout',
        },
      ]
    : [];
};

const updateConnection: QueueNodeAttributes['updateConnection'] = (
  node,
  _connection,
  connectionId,
) => {
  const payload: Payload = { ...node.payload };

  if (payload.queue_timeout) {
    payload.queue_timeout = {
      ...payload.queue_timeout,
      timeout_node_id: connectionId,
    };
  }

  return { ...node, payload };
};

const settingsLabel = {
  announcement_attributes: 'announce queue position',
  queue_timeout: 'timeout',
  wait_music_v2: 'custom wait music',
  callbackInfo: 'callback',
};

const createQueueNode = (props?: QueueAttributeFactoryProps) =>
  function QueueNode({ node, context }: QueueNodeProps): JSX.Element {
    const { withSettingsText, defaultQueueName } = props || {};
    const { extraData } = context;
    const { payload } = node;
    const queue = useFindQueue(payload.queue, extraData);

    const { text, settings } = useMemo(() => {
      if (!withSettingsText) {
        return { text: '', settings: [] };
      }

      const settings = Object.entries(settingsLabel).reduce(
        (settings: string[], [key, value]): string[] => {
          const isAnnouncementAttributes = key === 'announcement_attributes';
          const data = payload[key];

          if (isAnnouncementAttributes ? data?.play_announcement : data) {
            settings.push(value);
          }

          return settings;
        },
        [],
      );

      const joinedSettings = settings.join(', ');
      const firstSettingsLetter = joinedSettings[0]?.toUpperCase();
      const text = `${firstSettingsLetter}${joinedSettings.slice(1)}`;

      return { text, settings };
    }, [payload, withSettingsText]);

    const drawArchivedBadge = usePixiBadge({
      text: 'Archived',
      variant: 'error',
      offset: { x: 0, y: 28 },
    });

    return (
      <TextContent>
        {useMemo(
          () => (
            <>
              <TextEllipsis>
                <Text weight="normal" text="Queue" />
                <Text
                  weight="semibold"
                  text={queue?.name ?? payload.queue?.label ?? defaultQueueName}
                />
              </TextEllipsis>

              {queue?.is_archived && <Graphics draw={drawArchivedBadge} />}

              {withSettingsText && (
                <TextBreak offset={{ y: queue?.is_archived ? 38 : 5, x: 0 }}>
                  <Text weight="normal" text="Settings" />
                  <Text
                    weight="semibold"
                    text={settings.length > 0 ? text : 'None'}
                  />
                </TextBreak>
              )}
            </>
          ),
          [
            defaultQueueName,
            drawArchivedBadge,
            payload.queue?.label,
            queue,
            settings.length,
            text,
            withSettingsText,
          ],
        )}
      </TextContent>
    );
  };

const getQueueModalForFlow = (type: FlowType) => {
  switch (type) {
    case FlowType.FLOW_TYPE_CALL:
      return PhoneQueueModal;
    case FlowType.FLOW_TYPE_EMAIL:
      return EmailQueueModal;
    case FlowType.FLOW_TYPE_CHAT:
      return ChatQueueModal;
    case FlowType.FLOW_TYPE_MESSENGER:
      return MessengerQueueModal;
    default:
      return () => null;
  }
};

export const createQueueNodeAttributes = (
  type: FlowType,
  props?: QueueAttributeFactoryProps,
): QueueNodeAttributes => ({
  iconName: 'users',
  label: 'Queue',
  color: hexStringToNumber(flowNodeColor.green),
  helpText:
    'The queue is where you’d like your incoming conversations to automatically end up based on your company’s structure and priorities (e.g. Sales, Support).',
  translatePayload,
  updateConnection,
  NodeContent: createQueueNode(props),
  ActionModal: getQueueModalForFlow(type),
});

export const QueueNodeChatAttributes = createQueueNodeAttributes(
  FlowType.FLOW_TYPE_CHAT,
);

export const QueueNodeEmailAttributes = createQueueNodeAttributes(
  FlowType.FLOW_TYPE_EMAIL,
  { withSettingsText: true },
);

export const QueueNodePhoneAttributes = createQueueNodeAttributes(
  FlowType.FLOW_TYPE_CALL,
  { withSettingsText: true },
);

export const QueueNodeMessengerAttributes = createQueueNodeAttributes(
  FlowType.FLOW_TYPE_MESSENGER,
  { withSettingsText: true },
);
